import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Zap } from "lucide-react";

const CATEGORIES = [
  { value: "fruits", label: "Плодове" },
  { value: "vegetables", label: "Зеленчуци" },
  { value: "legumes", label: "Бобови / Зърнени" },
  { value: "grains", label: "Зърна / Хляб" },
  { value: "dairy", label: "Млечни" },
  { value: "meat_fish", label: "Месо / Риба" },
  { value: "other", label: "Други" },
];

export function FoodFormDialog({ open, onClose, form, setForm, editFood, onSave, isDark }) {
  const inputCls = `w-full px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`;

  const calcDil = () => {
    const p = parseFloat(form.protein_100g) || 0;
    const c = parseFloat(form.carbs_100g) || 0;
    const fi = parseFloat(form.fiber_100g) || 0;
    const cal = parseFloat(form.calories_100g) || 0;
    const dil = (0.56 * p + Math.max(c - fi, 0)).toFixed(2);
    const dii = cal > 0 ? ((dil * 4 / cal) * 100).toFixed(1) : "";
    setForm(f => ({ ...f, dil_100g: dil, dii_percent: dii }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white'}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? 'text-white' : ''}>{editFood ? `Редакция: ${editFood.food}` : "Нова храна"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Име на храната</label>
            <input className={inputCls} value={form.food}
              onChange={e => setForm(f => ({ ...f, food: e.target.value }))}
              data-testid="food-form-name" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Категория</label>
            <select className={inputCls} value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Протеин (100g)</label>
              <input className={inputCls} type="number" step="0.1" value={form.protein_100g}
                onChange={e => setForm(f => ({ ...f, protein_100g: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Въглех. (100g)</label>
              <input className={inputCls} type="number" step="0.1" value={form.carbs_100g}
                onChange={e => setForm(f => ({ ...f, carbs_100g: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Фибри (100g)</label>
              <input className={inputCls} type="number" step="0.1" value={form.fiber_100g}
                onChange={e => setForm(f => ({ ...f, fiber_100g: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Мазнини (100g)</label>
              <input className={inputCls} type="number" step="0.1" value={form.fat_100g}
                onChange={e => setForm(f => ({ ...f, fat_100g: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Нас. мазн. (100g)</label>
              <input className={inputCls} type="number" step="0.1" value={form.sat_fat_100g}
                onChange={e => setForm(f => ({ ...f, sat_fat_100g: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Калории (100g)</label>
              <input className={inputCls} type="number" step="0.1" value={form.calories_100g}
                onChange={e => setForm(f => ({ ...f, calories_100g: e.target.value }))} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={calcDil} data-testid="calc-dil-btn">
              <Zap className="w-3 h-3 mr-1" /> Изчисли DIL
            </Button>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              0.56 x протеин + (въглех. - фибри)
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">DIL / 100g</label>
              <input className={inputCls} type="number" step="0.01" value={form.dil_100g}
                onChange={e => setForm(f => ({ ...f, dil_100g: e.target.value }))}
                data-testid="food-form-dil" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">DII %</label>
              <input className={inputCls} type="number" step="0.1" value={form.dii_percent}
                onChange={e => setForm(f => ({ ...f, dii_percent: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Omega 6:3</label>
              <input className={inputCls} type="number" step="0.1" value={form.omega_6_3_ratio}
                onChange={e => setForm(f => ({ ...f, omega_6_3_ratio: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>Отказ</Button>
            <Button size="sm" className="gradient-primary text-white" onClick={onSave} data-testid="save-food-btn">
              {editFood ? "Обнови" : "Добави"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
